import type { Influencer } from '@foodfluence/shared';
import type { LocalCreatorsProfileImage } from '../types';
import type { MarketplaceFormSnapshot } from './marketplaceProfileTypes';

/** Public marketplace bio: no minimum length; max enforced in API and DTO builders. */
export const MARKETPLACE_BIO_MAX_CHARS = 750;

/** Client id for a bootstrapped profile photo copied from the Foodfluence account avatar. */
export const ACCOUNT_DEFAULT_PROFILE_IMAGE_ID = 'account-default';

/** Default creator link segment from Instagram/TikTok handle when the profile has no slug yet. */
export function buildDefaultSlugFromInfluencer(
  influencer: Pick<Influencer, 'instagramHandle' | 'tiktokHandle'> | null | undefined
): string {
  const raw = influencer?.instagramHandle?.trim() || influencer?.tiktokHandle?.trim() || '';
  return raw.replace(/^@+/, '').replace(/\s+/g, '');
}

/** Extract Firebase Storage object path from a download URL, when applicable. */
export function firebaseStoragePathFromDownloadUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('firebasestorage.googleapis.com')) {
      return null;
    }
    const match = parsed.pathname.match(/\/o\/(.+)$/);
    if (!match?.[1]) {
      return null;
    }
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/** Extract GCS object path from `storage.googleapis.com/{bucket}/{object}` URLs. */
export function gcsStoragePathFromDownloadUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'storage.googleapis.com') {
      return null;
    }
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length < 2) {
      return null;
    }
    return parts.slice(1).join('/');
  } catch {
    return null;
  }
}

/** Best-effort storage object path for an account profile photo URL. */
export function accountPhotoStoragePathFromUrl(url: string, influencerId: string): string {
  return (
    firebaseStoragePathFromDownloadUrl(url) ??
    gcsStoragePathFromDownloadUrl(url) ??
    `marketplaceCreatorProfiles/${influencerId}/profile/account-default`
  );
}

/**
 * Seed `MarketplaceProfile.profileImage` from the main Foodfluence account photo without re-uploading.
 * Uses `downloadUrl` for display; `storagePath` is the Firebase path when known, otherwise a stable sentinel.
 */
export function buildDefaultProfileImageFromAccountPhoto(
  profilePicUrl: string,
  influencerId: string
): LocalCreatorsProfileImage | null {
  const trimmed = profilePicUrl.trim();
  if (!trimmed) {
    return null;
  }
  return {
    id: ACCOUNT_DEFAULT_PROFILE_IMAGE_ID,
    storagePath: accountPhotoStoragePathFromUrl(trimmed, influencerId),
    url: trimmed,
    contentType: 'image/jpeg',
  };
}

/** Default slug and profile photo for a creator with no marketplace profile yet. */
export function buildInitialMarketplaceDefaults(
  influencer: Pick<Influencer, 'id' | 'instagramHandle' | 'tiktokHandle' | 'profile_pic_url'>
): Partial<MarketplaceFormSnapshot> {
  const defaults: Partial<MarketplaceFormSnapshot> = {};
  const slug = buildDefaultSlugFromInfluencer(influencer);
  if (slug) {
    defaults.slug = slug;
  }
  if (influencer.profile_pic_url) {
    const profileImage = buildDefaultProfileImageFromAccountPhoto(
      influencer.profile_pic_url,
      influencer.id
    );
    if (profileImage) {
      defaults.profileImage = profileImage;
    }
  }
  return defaults;
}

/** True when the marketplace photo is the seeded account avatar (not a custom upload). */
export function isAccountDefaultProfileImage(
  img: LocalCreatorsProfileImage | null | undefined,
  accountPhotoUrl?: string | null
): boolean {
  if (!img) {
    return false;
  }
  if (img.id === ACCOUNT_DEFAULT_PROFILE_IMAGE_ID) {
    return true;
  }
  const account = accountPhotoUrl?.trim();
  return Boolean(account && img.url.trim() === account);
}

/** True when the creator uploaded a marketplace-specific photo distinct from their account avatar. */
export function hasCustomMarketplaceProfileImage(
  img: LocalCreatorsProfileImage | null | undefined,
  accountPhotoUrl?: string | null
): boolean {
  return Boolean(img) && !isAccountDefaultProfileImage(img, accountPhotoUrl);
}
